package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCloseIcon
import dev.lingvo.site.ds.LIconButton
import dev.lingvo.site.ds.LChord
import dev.lingvo.site.ds.LPresencePhase
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.rememberLPresence
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal data class EditorShortcutRow(
    val action: String,
    val keys: List<String>,
    val requirement: EditorShortcutRequirement = EditorShortcutRequirement.READ,
)

internal enum class EditorShortcutRequirement {
    READ,
    WRITE,
    APPROVE,

    /**
     * Spends the workspace's machine-translation credits.
     *
     * A fourth requirement rather than [WRITE], because `POST /mt/translate` asks for the
     * `mt:spend` scope *and* role >= ADMIN. Alt+T was listed here for anyone who could write, so
     * the sheet advertised a chord that answered 403 — the same offer-then-refuse the source row's
     * sparkle and the Assist generate button used to make.
     */
    SPEND,
}

internal val editorShortcutRows = listOf(
    EditorShortcutRow(tr("editor.editor_modal_surfaces.previous_next_key", "Previous / next key"), listOf("↑", "↓")),
    EditorShortcutRow(tr("editor.editor_modal_surfaces.focus_target", "Focus target"), listOf("Enter")),
    EditorShortcutRow(tr("editor.editor_modal_surfaces.close_top_surface", "Close top surface"), listOf("Esc")),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.save_and_next", "Save and next"),
        listOf("⌘/Ctrl", "Enter"),
        EditorShortcutRequirement.WRITE,
    ),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.approve_and_next", "Approve and next"),
        listOf("⌘/Ctrl", "Shift", "Enter"),
        EditorShortcutRequirement.APPROVE,
    ),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.save", "Save"),
        listOf("⌘/Ctrl", "S"),
        EditorShortcutRequirement.WRITE,
    ),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.next_untranslated", "Next untranslated"),
        listOf("⌘/Ctrl", "Shift", "↓"),
    ),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.next_qa_issue", "Next QA issue"),
        listOf("⌘/Ctrl", "Shift", "E"),
    ),
    EditorShortcutRow(tr("editor.editor_modal_surfaces.find_a_key", "Find a key"), listOf("/")),
    EditorShortcutRow(tr("editor.editor_modal_surfaces.copy_source", "Copy source"), listOf("Alt", "C")),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.use_assist_result", "Use Assist result"),
        listOf("Alt", "1–9"),
        EditorShortcutRequirement.WRITE,
    ),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.insert_next_placeholder", "Insert next placeholder"),
        listOf("Alt", "P"),
        EditorShortcutRequirement.WRITE,
    ),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.insert_opening_quote", "Insert opening quote"),
        listOf("Alt", "Q"),
        EditorShortcutRequirement.WRITE,
    ),
    EditorShortcutRow(
        tr("editor.editor_modal_surfaces.insert_closing_quote", "Insert closing quote"),
        listOf("Alt", "W"),
        EditorShortcutRequirement.WRITE,
    ),
    EditorShortcutRow(tr("editor.editor_modal_surfaces.history", "History"), listOf("Alt", "H")),
    EditorShortcutRow(
        tr(
            "editor.editor_modal_surfaces.generate_suggestion_with_your_default_provider",
            "Generate suggestion with your default provider",
        ),
        listOf("Alt", "T"),
        EditorShortcutRequirement.SPEND,
    ),
    EditorShortcutRow(tr("editor.editor_modal_surfaces.show_shortcuts", "Show shortcuts"), listOf("?")),
    EditorShortcutRow(tr("editor.editor_modal_surfaces.keyboard_shortcuts", "Keyboard shortcuts"), listOf("Alt", "/")),
)

internal fun editorShortcutRowsFor(
    capability: ResolvedEditorCapability,
): List<EditorShortcutRow> = editorShortcutRows.filter { row ->
    when (row.requirement) {
        EditorShortcutRequirement.READ -> true
        EditorShortcutRequirement.WRITE -> capability.canWriteTranslations
        EditorShortcutRequirement.APPROVE -> capability.canApproveTranslations
        EditorShortcutRequirement.SPEND -> capability.canSpendCredits
    }
}

@Composable
internal fun EditorModalSurfaces(
    modal: EditorModalSurface,
    onClose: () -> Unit,
    onConfirmClear: () -> Unit,
    capability: ResolvedEditorCapability? = null,
    /**
     * The queue's bulk automations, when this editor has any.
     *
     * Defaulted so the dozen fixtures that mount this surface for the shortcut sheet keep working;
     * `BULK_CONFIRMATION` simply cannot be reached without one, which is also true in production —
     * only the queue's own buttons open it.
     */
    automation: EditorQueueAutomationStore? = null,
) {
    // **The surface that is leaving is still the surface that was there.**
    //
    // `closeModal()` flips `modal` to `NONE` in the frame the reader presses Escape, so an exit
    // needs two things the mount-only form had neither of: a presence lifecycle to keep rendering
    // through the exit window, and a memory of *which* dialog is going, since `modal` no longer
    // says. Both of these were the last callers plan 001 named and never got.
    val presence = rememberLPresence(modal != EditorModalSurface.NONE, Motion.OverlayExitMs)
    var leaving by remember { mutableStateOf(EditorModalSurface.NONE) }
    if (modal != EditorModalSurface.NONE) leaving = modal
    if (!presence.rendered) return

    val shown = leaving
    if (shown == EditorModalSurface.CLEAR_CONFIRMATION && capability?.canWriteTranslations == false) return
    if (shown == EditorModalSurface.BULK_CONFIRMATION && automation == null) return
    val exiting = presence.phase == LPresencePhase.EXITING
    val presenceValue = presence.phase.attributeValue
    val visibleShortcutRows = capability?.let(::editorShortcutRowsFor) ?: editorShortcutRows
    Div(
        EditorModalScrimStyle.toModifier()
            .attr("data-editor-scrim", "true")
            .attr("aria-hidden", "true")
            .attr("data-presence", presenceValue)
            .then(if (exiting) Modifier.attr("inert", "") else Modifier)
            .toAttrs { onClick { onClose() } },
    )
    Div(
        EditorModalSurfaceStyle.toModifier()
            .attr("id", EDITOR_MODAL_SURFACE_ID)
            .attr("data-editor-modal", "true")
            .attr("data-presence", presenceValue)
            // Inert and hidden while leaving, so a dialog on its way out cannot take a Tab or be
            // read out over the surface the reader is returning to.
            .then(if (exiting) Modifier.attr("inert", "").attr("aria-hidden", "true") else Modifier)
            .role("dialog")
            .attr("aria-modal", "true")
            .attr("aria-label", when (shown) {
                EditorModalSurface.CLEAR_CONFIRMATION -> tr("editor.editor_modal_surfaces.clear_translation", "Clear translation")
                EditorModalSurface.SHORTCUTS -> tr("editor.editor_modal_surfaces.keyboard_shortcuts", "Keyboard shortcuts")
                EditorModalSurface.BULK_CONFIRMATION ->
                    tr("editor.editor_modal_surfaces.confirm_a_bulk_action", "Confirm a bulk action")
                EditorModalSurface.NONE -> tr("editor.editor_modal_surfaces.editor_dialog", "Editor dialog")
            })
            .attr("tabindex", "-1")
            .toAttrs(),
    ) {
        Row(
            EditorModalHeaderStyle.toModifier(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            H2(Modifier.fontSize(TypeScale.Body).toAttrs()) {
                Text(
                    when (shown) {
                        EditorModalSurface.CLEAR_CONFIRMATION -> t("editor.editor_modal_surfaces.clear_translation", "Clear translation")
                        EditorModalSurface.SHORTCUTS -> t("editor.editor_modal_surfaces.keyboard_shortcuts", "Keyboard shortcuts")
                        // The count is the title, per the automation rule: a confirm whose heading
                        // says "Confirm" makes the reader hunt the body for the number they are
                        // agreeing to.
                        EditorModalSurface.BULK_CONFIRMATION ->
                            editorBulkConfirmTitle(automation?.confirmation)
                        EditorModalSurface.NONE -> t("editor.editor_modal_surfaces.editor_dialog", "Editor dialog")
                    },
                )
            }
            Spacer()
            LIconButton(
                label = t("editor.editor_modal_surfaces.close_dialog", "Close dialog"),
                onClick = onClose,
                modifier = EditorCoarseTargetStyle.toModifier()
                    .attr("data-editor-coarse-target", "close"),
                size = LButtonSize.SM,
            ) {
                LCloseIcon()
            }
        }
        when (shown) {
            EditorModalSurface.CLEAR_CONFIRMATION -> {
                Column(EditorModalBodyStyle.toModifier().gap(Space.MD)) {
                    Span(Modifier.toAttrs()) {
                        Text(t("editor.editor_modal_surfaces.clear_the_current_translation_this_keeps", "Clear the current translation? This keeps the key and source text."))
                    }
                    Row(Modifier.fillMaxWidth().gap(Space.SM)) {
                        LButton(
                            label = t("editor.editor_modal_surfaces.cancel", "Cancel"),
                            onClick = onClose,
                            modifier = EditorCoarseTargetStyle.toModifier()
                                .attr("id", EDITOR_CLEAR_MODAL_INITIAL_FOCUS_ID)
                                .attr("data-editor-coarse-target", "close"),
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                        )
                        LButton(
                            label = t("editor.editor_modal_surfaces.clear_translation", "Clear translation"),
                            onClick = onConfirmClear,
                            variant = LButtonVariant.DANGER,
                            size = LButtonSize.SM,
                        )
                    }
                }
            }

            EditorModalSurface.SHORTCUTS -> {
                Column(EditorModalBodyStyle.toModifier().gap(Space.SM)) {
                    visibleShortcutRows.forEach { shortcut ->
                        Row(
                            Modifier.fillMaxWidth().gap(Space.SM),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Span(
                                Modifier.flexGrow(1).fontSize(TypeScale.Small).toAttrs(),
                            ) {
                                Text(shortcut.action)
                            }
                            LChord(shortcut.keys)
                        }
                    }
                    LButton(
                        label = t("editor.editor_modal_surfaces.close", "Close"),
                        onClick = onClose,
                        modifier = EditorCoarseTargetStyle.toModifier()
                            .attr("id", EDITOR_SHORTCUTS_MODAL_INITIAL_FOCUS_ID)
                            .attr("data-editor-coarse-target", "close"),
                        variant = LButtonVariant.SECONDARY,
                        size = LButtonSize.SM,
                    )
                }
            }

            EditorModalSurface.BULK_CONFIRMATION -> {
                if (automation != null) {
                    Column(EditorModalBodyStyle.toModifier().gap(Space.MD)) {
                        EditorBulkConfirmBody(automation = automation, onClose = onClose)
                    }
                }
            }

            EditorModalSurface.NONE -> Unit
        }
    }
}
